import { useEffect, useState } from 'react';
import { envConfig } from '../config/authConfig';

/**
 * Feature flags returned by the backend at GET /api/config/features.
 * Default values mirror appsettings.json (everything on) so the UI degrades
 * to "show feature" if the request fails — change the appsettings default
 * if you ever want a different fallback.
 */
export interface Features {
  userCvUi: boolean;
}

const DEFAULTS: Features = {
  userCvUi: true,
};

// Module-level cache so we only fetch once per page load across all hook consumers.
let cached: Features | null = null;
let inflight: Promise<Features> | null = null;
const subscribers = new Set<(f: Features) => void>();

function fetchFeatures(): Promise<Features> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;

  inflight = fetch(`${envConfig.apiBaseUrl}/api/config/features`, {
    headers: { 'Accept': 'application/json' },
  })
    .then(async (res) => {
      if (!res.ok) throw new Error(`features fetch failed: ${res.status}`);
      const body = await res.json();
      const f: Features = {
        userCvUi: typeof body?.userCvUi === 'boolean' ? body.userCvUi : DEFAULTS.userCvUi,
      };
      cached = f;
      subscribers.forEach((cb) => cb(f));
      return f;
    })
    .catch((err) => {
      console.warn('Falling back to default feature flags:', err);
      cached = DEFAULTS;
      subscribers.forEach((cb) => cb(DEFAULTS));
      return DEFAULTS;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

/**
 * Returns the current feature flag set. While the initial fetch is in flight
 * the defaults (everything on) are returned, so the UI never flashes "hidden"
 * for users when the backend is reachable.
 */
export function useFeatures(): Features {
  const [features, setFeatures] = useState<Features>(cached ?? DEFAULTS);

  useEffect(() => {
    if (cached) {
      setFeatures(cached);
      return;
    }
    const handler = (f: Features) => setFeatures(f);
    subscribers.add(handler);
    fetchFeatures();
    return () => {
      subscribers.delete(handler);
    };
  }, []);

  return features;
}
